import { AREA_NOT_FOUND } from 'components/AreaController/feature/area.constants';
import { AxiosError } from 'axios';
import { ProcessPaymentRequest } from 'api/payment/types/process-payment-request.type';
import { ResponseError } from 'types/error.types';
import { appInsights } from 'utils/telemetry/app-insights';
import { areaApiService } from 'api/area/area.api.service';
import { cleanMarketplaceRecipient } from 'containers/PDPContainer/feature/marketplaceRecipient/marketplaceRecipients.actions';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { parseAxiosError } from 'utils/errors';
import { paymentApiService } from 'api/payment/payment.api.service';
import { safeTrackEvent } from 'utils/appInsights-track-event';

export const processPayment = createAsyncThunk(
  'processPayment/fetch',
  async (request: ProcessPaymentRequest, { rejectWithValue }) => {
    try {
      const data = await paymentApiService.processPayment(request);

      if (data.saleNumber !== undefined && data.saleNumber !== null) {
        try {
          sessionStorage.setItem('saleNumber', String(data.saleNumber));
        } catch (error) {
          safeTrackEvent(
            `Sale Number Storage Error - ${window.COUNTRY || 'US'}`,
            {
              error: error instanceof Error ? error.message : 'Unknown error',
            },
          );
        }
      }

      cleanMarketplaceRecipient().catch((error) => {
        safeTrackEvent(
          `Clean MarketPlace Recipients Error - ${window.COUNTRY || 'US'}`,
          {
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        );
      });

      return data;
    } catch (ex) {
      const error = ex as AxiosError;
      const parsedError = parseAxiosError(error);

      safeTrackEvent(`Process Payment API Error - ${window.COUNTRY || 'US'}`, {
        error: error.message || undefined,
        step: 'Process Payment API Error',
        responseCode: error.code || undefined,
        errorResponse: JSON.stringify(error.response),
        paymentResponse: JSON.stringify(error.response?.data),
        status: error.response?.status || undefined,
      });

      return rejectWithValue(parsedError);
    }
  },
);

export const getCountries = createAsyncThunk(
  'getCountries/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const data = await paymentApiService.getCountries();
      return data;
    } catch (err) {
      return rejectWithValue(parseAxiosError(<AxiosError>err));
    }
  },
);

export const getStates = createAsyncThunk(
  'getStates/fetch',
  async (countryId: number, { rejectWithValue }) => {
    try {
      const data = await paymentApiService.getStates(countryId);
      return data;
    } catch (err) {
      return rejectWithValue(parseAxiosError(<AxiosError>err));
    }
  },
);

export const getArea = createAsyncThunk(
  'area/fetch',
  async (name: string, { rejectWithValue }) => {
    try {
      const { data } = await areaApiService.getAreas(name, 1);

      if (!data.length) {
        const error: ResponseError = {
          message: 'Area not found',
          data: { key: AREA_NOT_FOUND },
          status: 404,
        };
        return rejectWithValue(error);
      }

      return {
        id: data[0].id,
        cities: data[0].cities,
        state: data[0].stateCode,
        country: data[0].countryCode,
      };
    } catch (err) {
      return rejectWithValue(parseAxiosError(err as AxiosError));
    }
  },
);

export const isEmailAlreadyExist = createAsyncThunk(
  'emailAleadyExist/fetch',
  async (email: string, { rejectWithValue }) => {
    try {
      const data = await paymentApiService.isEmailAlreadyExist(email);
      return data;
    } catch (ex) {
      const error = ex as AxiosError;

      if (error.response?.data) {
        const responseError: ResponseError = {
          message: 'Something went wrong. Please try after some time',

          data: { key: 'Email validation failed' },

          status: 404,
        };

        return rejectWithValue(responseError);
      }

      return rejectWithValue(parseAxiosError(error));
    }
  },
);
